import React from "react";
import * as Yup from "yup";
import {
  Form,
  Row,
  TextBox,
  Col,
  Checkbox,
  Select,
  Panel,
  Button,
  ValidationSummary,
  HelpIconMode,
} from "@countysuite/ui";
import { getInputValueByName } from "utils/getInputValueByName";
import {
  LackOfProsecutionTemplateDto,
  DispositionCategoryType,
} from "../../LackOfProsecutionTypes";
import { HelpIconButton } from "components/HelpIconButton/HelpIconButton";
import { Official } from "types/CivilTypes";

export interface Props extends LackOfProsecutionTemplateDto {
  changeDetails: (newDetails: any) => void;
  onSelectTemplate: (lopTemplateId: number) => void;
  lopTemplates: LackOfProsecutionTemplateDto[];
  onSave: () => void;
  showAddTemplateModal: () => void;
  signatures: Official[];
  dispositionCategories: DispositionCategoryType[];
}

export const TemplateDetailsPanel: React.FC<Props> = (props) => {
  const updateValueByInputName = (e: any) => {
    let value = getInputValueByName(e);
    props.changeDetails(value);
  };

  const handleSelectTemplate = (e: any) => {
    const selectedId = Number(e.target.value);
    if (selectedId > 0) {
      props.onSelectTemplate(selectedId);
    }
  };

  const handleSelectDispositionCategoryType = (e: any) => {
    let id = Number(e.target.value);
    const dct = props.dispositionCategories.find((dct) => dct.id === id);
    props.changeDetails({
      dispositionCategoryID: dct?.dispositionCategoryID,
      dispositionTypeID: dct?.dispositionTypeID,
    });
  };

  const templates = [...props.lopTemplates];
  templates.unshift({
    id: 0,
    name: "< Select >",
    defaultDaysOfInactivity: 0,
    sequence: 999,
    visible: false,
    defaultSignatureOfficialID: props.defaultSignatureOfficialID,
    excludeZeroDollarJudgments: false,
    dispositionTypeID: 0,
    dispositionCategoryID: 0,
    hideDeadlineDate: false,
    referenceDateLabel: "",
    showParticipantListButton: false,
    showLettersSelect: false,
    lettersIDs: [],
    showPretrialDatePicker: false,
    ignoreJudgementCases: false,
  });

  const formValidation = Yup.object().shape({
    name: Yup.string().required("Please enter a name for this template."),
    defaultDaysOfInactivity: Yup.number()
      .transform((cv) => (isNaN(cv) ? undefined : cv))
      .required("Please enter a number for days of inactivity.")
      .min(1, "The days of inactivity must be at least 1."),
    sequence: Yup.number()
      .transform((cv) => (isNaN(cv) ? undefined : cv))
      .required(
        "Please enter the position that this template appears in the dropdown."
      )
      .min(1, "The position in dropdowns must be at least 1")
      .max(9999, "The position in dropdowns cannot exceed 9999."),
  });
  const dct = props.dispositionCategories.find(
    (dct) =>
      dct.dispositionCategoryID === props.dispositionCategoryID &&
      dct.dispositionTypeID === props.dispositionTypeID
  );
  return (
    <Form
      onSubmit={props.onSave}
      validationSchema={formValidation}
      values={props}
      showErrors={false}
    >
      {(formProps) => (
        <Panel
          title="Template Details"
          buttons={
            <>
              <HelpIconButton mode={HelpIconMode.Modal}>
                <p>
                  Please choose a name for your template, where it's positioned
                  in dropdown menus, and if it's currently visible in the
                  system.
                </p>
                <p>
                  For Days of Inactivity, this is how many days that the
                  template will subtract from today's date when trying to
                  determine the Date of Inactivity for the case search.
                </p>
              </HelpIconButton>
              <Button
                type="button"
                color="success"
                onClick={formProps.handleSubmit}
                disabled={!props.id}
              >
                Save
              </Button>
              <Button type="button" onClick={props.showAddTemplateModal}>
                Add New Template
              </Button>
            </>
          }
        >
          <Row>
            <Col xs={12}>
              <ValidationSummary errors={formProps.errorSummary.errors} />
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6}>
              <Select
                data-testid="templateId"
                name="lopTemplateId"
                label="Selected Template"
                onChange={handleSelectTemplate}
                options={templates}
                value={props.id}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6}>
              <TextBox
                data-testid="templateName"
                name="name"
                label="Template Name"
                onChange={updateValueByInputName}
                value={props.name}
                disabled={!props.id}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6}>
              <TextBox
                data-testid="sequence"
                name="sequence"
                label="Sequence Number In Dropdowns"
                onChange={updateValueByInputName}
                value={props.sequence}
                disabled={!props.id}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6}>
              <TextBox
                data-testid="defaultDaysOfInactivity"
                name="defaultDaysOfInactivity"
                label="Days of Inactivity"
                onChange={updateValueByInputName}
                value={props.defaultDaysOfInactivity}
                disabled={!props.id}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6}>
              <Select
                label="Choose Disposition Type to use when Terminating Cases"
                onChange={handleSelectDispositionCategoryType}
                name="dispositionCategoryTypeID"
                value={dct?.id ?? 0}
                options={props.dispositionCategories}
                disabled={!props.id}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={3}>
              <Checkbox
                data-testid="visible"
                name="visible"
                onChange={updateValueByInputName}
                checked={props.visible}
                disabled={!props.id}
              >
                Visible in Dropdowns
              </Checkbox>
            </Col>
            <Col xs={12} md={3}>
              <Checkbox
                name="excludeZeroDollarJudgments"
                checked={props.excludeZeroDollarJudgments}
                onChange={updateValueByInputName}
                disabled={!props.id}
              >
                Exclude cases with $0.00 judgments
              </Checkbox>
            </Col>
            <Col xs={12} md={3}>
              <Checkbox
                name="ignoreJudgementCases"
                checked={props.ignoreJudgementCases}
                onChange={updateValueByInputName}
                disabled={!props.id}
              >
                Exclude cases that have a judgment
              </Checkbox>
            </Col>
            <Col xs={12} md={3}>
              <Checkbox
                name="showPretrialDatePicker"
                checked={props.showPretrialDatePicker}
                onChange={updateValueByInputName}
                disabled={!props.id}
              >
                Show Pretrial Date Picker
              </Checkbox>
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={3}>
              <Checkbox
                name="hideDeadlineDate"
                checked={props.hideDeadlineDate}
                onChange={updateValueByInputName}
                disabled={!props.id}
              >
                Hide Deadline Date
              </Checkbox>
            </Col>
            <Col xs={12} md={3}>
              <Checkbox
                name="showParticipantListButton"
                checked={props.showParticipantListButton}
                onChange={updateValueByInputName}
                disabled={!props.id}
              >
                Show Participant List Button
              </Checkbox>
            </Col>
            <Col xs={12} md={3}>
              <Checkbox
                name="showLettersSelect"
                checked={props.showLettersSelect}
                onChange={updateValueByInputName}
                disabled={!props.id}
              >
                Add Checkbox Column To Letters Step
              </Checkbox>
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6}>
              <Select
                name="defaultSignatureOfficialID"
                label="Default Signature"
                onChange={updateValueByInputName}
                value={props.defaultSignatureOfficialID}
                options={props.signatures}
                disabled={!props.id}
              />
            </Col>
          </Row>
        </Panel>
      )}
    </Form>
  );
};

(TemplateDetailsPanel as any).displayName = "TemplateDetailsPanel";
